import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";

export default function ContactForm({ defaultService = "", className = "" }) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    message: "",
    website_url: "", // Honeypot field — mandatory antispam
  });

  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // null | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState("");
  const fileInputRef = useRef(null);

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) {
      errs.name = "Angiv venligst dit fulde navn";
    }
    if (!formData.phone.trim()) {
      errs.phone = "Angiv venligst et telefonnummer";
    } else if (!/^[+0-9\s-]{8,20}$/.test(formData.phone.trim())) {
      errs.phone = "Ugyldigt telefonnummer";
    }
    if (!formData.email.trim()) {
      errs.email = "Angiv venligst din emailadresse";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errs.email = "Ugyldig emailadresse";
    }
    if (!formData.message.trim()) {
      errs.message = "Skriv venligst en kort beskrivelse af opgaven";
    }
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleFilesAdded = (incomingFiles) => {
    setFileError("");
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    const maxPerFile = 10 * 1024 * 1024; // 10 MB
    const maxTotal = 20 * 1024 * 1024; // 20 MB
    const maxCount = 5;

    const currentTotalFiles = files.length + incomingFiles.length;
    if (currentTotalFiles > maxCount) {
      setFileError(`Du kan maksimalt vedhæfte ${maxCount} billeder.`);
      return;
    }

    const validNewFiles = [];
    let currentTotalSize = files.reduce((acc, f) => acc + f.size, 0);

    for (const file of incomingFiles) {
      if (!allowedTypes.includes(file.type) && !file.name.match(/\.(jpe?g|png|webp|gif)$/i)) {
        setFileError(`Filen "${file.name}" er ikke et understøttet billedformat (kun JPG, PNG, WEBP, GIF).`);
        return;
      }
      if (file.size > maxPerFile) {
        setFileError(`Filen "${file.name}" fylder mere end 10 MB.`);
        return;
      }
      currentTotalSize += file.size;
      if (currentTotalSize > maxTotal) {
        setFileError("Den samlede størrelse af de vedhæftede billeder må maksimalt være 20 MB.");
        return;
      }
      validNewFiles.push(file);
    }

    setFiles((prev) => [...prev, ...validNewFiles]);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFilesAdded(Array.from(e.target.files));
      // Reset input value so re-selecting the same file works
      e.target.value = "";
    }
  };

  const handleRemoveFile = (indexToRemove) => {
    setFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
    setFileError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    setStatus(null);
    setErrorMessage("");

    try {
      const payload = new FormData();
      payload.append("name", formData.name.trim());
      payload.append("phone", formData.phone.trim());
      payload.append("email", formData.email.trim());
      if (formData.address?.trim()) {
        payload.append("address", formData.address.trim());
      }
      if (defaultService) {
        payload.append("service", defaultService);
      }
      payload.append("message", formData.message.trim());
      payload.append("website_url", formData.website_url); // Honeypot

      // Attach all selected photos
      files.forEach((file) => {
        payload.append("photos", file);
      });

      const response = await fetch("/_api/webform/submit", {
        method: "POST",
        body: payload,
      });

      if (response.ok) {
        setStatus("success");
        setFormData({
          name: "",
          phone: "",
          email: "",
          address: "",
          message: "",
          website_url: "",
        });
        setFiles([]);
        setFileError("");
      } else {
        const data = await response.json().catch(() => null);
        setStatus("error");
        setErrorMessage(
          data?.message || "Der opstod en fejl ved indsendelsen. Prøv venligst igen eller ring på 42 16 92 30."
        );
      }
    } catch (err) {
      setStatus("error");
      setErrorMessage(
        "Kunne ikke oprette forbindelse til serveren. Tjek din internetforbindelse eller ring direkte på 42 16 92 30."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`bg-white rounded-2xl p-6 sm:p-8 md:p-10 shadow-xl border border-[#E8E1D5] ${className}`}>
      <div className="mb-6">
        <h3 className="text-xl sm:text-2xl font-bold text-[#14291E] tracking-tight">
          Få et uforpligtende tilbud
        </h3>
        <p className="text-sm text-[#526D5F] mt-1.5 leading-relaxed">
          Beskriv dit træarbejde herunder eller vedhæft billeder af opgaven, så vender jeg hurtigt tilbage med en skarp vurdering.
        </p>
      </div>

      {status === "success" ? (
        <div className="bg-[#EBF7F0] border border-[#2E8B57]/30 rounded-xl p-6 text-center space-y-3 animate-in fade-in">
          <div className="w-14 h-14 bg-[#23583C] text-white rounded-full flex items-center justify-center mx-auto text-2xl shadow-sm">
            <i className="fa-solid fa-check" aria-hidden="true" />
          </div>
          <h4 className="text-lg font-bold text-[#153B27]">Mange tak for din henvendelse!</h4>
          <p className="text-sm text-[#27533B] max-w-md mx-auto leading-relaxed">
            Din besked er modtaget. Jeg gennemgår din opgave og kontakter dig hurtigst muligt på telefon eller email.
          </p>
          <div className="pt-2">
            <button
              type="button"
              onClick={() => setStatus(null)}
              className="text-xs font-semibold text-[#18462E] underline hover:text-[#D9691E] transition-colors cursor-pointer"
            >
              Send endnu en besked
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {/* Honeypot field for antispam — hidden from humans with sr-only + absolute positioning */}
          <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
            <label htmlFor="website_url">Website Url</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              tabIndex="-1"
              autoComplete="off"
              value={formData.website_url}
              onChange={handleChange}
            />
          </div>

          {/* Form Error Banner */}
          {status === "error" && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm flex items-start gap-3">
              <i className="fa-solid fa-triangle-exclamation text-red-600 mt-0.5" aria-hidden="true" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-[#2C493B] mb-1.5">
                Dit navn <span className="text-[#D9691E]">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="F.eks. Jens Jensen"
                className={`w-full px-4 py-3 rounded-lg border text-sm text-[#14291E] bg-[#FDFCFB] placeholder-[#94A39B] transition-colors focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1E4231] ${
                  errors.name ? "border-red-500 ring-1 ring-red-500" : "border-[#D6CEBF]"
                }`}
              />
              {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-[#2C493B] mb-1.5">
                Telefonnummer <span className="text-[#D9691E]">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="F.eks. 42 16 92 30"
                className={`w-full px-4 py-3 rounded-lg border text-sm text-[#14291E] bg-[#FDFCFB] placeholder-[#94A39B] transition-colors focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1E4231] ${
                  errors.phone ? "border-red-500 ring-1 ring-red-500" : "border-[#D6CEBF]"
                }`}
              />
              {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-[#2C493B] mb-1.5">
                Emailadresse <span className="text-[#D9691E]">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="din@email.dk"
                className={`w-full px-4 py-3 rounded-lg border text-sm text-[#14291E] bg-[#FDFCFB] placeholder-[#94A39B] transition-colors focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1E4231] ${
                  errors.email ? "border-red-500 ring-1 ring-red-500" : "border-[#D6CEBF]"
                }`}
              />
              {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
            </div>

            {/* Address (optional) */}
            <div>
              <label htmlFor="address" className="block text-xs font-bold uppercase tracking-wider text-[#2C493B] mb-1.5">
                Adresse <span className="text-xs font-normal normal-case text-[#6E887A]">(valgfrit)</span>
              </label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="F.eks. Skovvej 12, 8600 Silkeborg"
                className="w-full px-4 py-3 rounded-lg border border-[#D6CEBF] text-sm text-[#14291E] bg-[#FDFCFB] placeholder-[#94A39B] transition-colors focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1E4231]"
              />
            </div>
          </div>

          {/* Message */}
          <div>
            <label htmlFor="message" className="block text-xs font-bold uppercase tracking-wider text-[#2C493B] mb-1.5">
              Beskrivelse af opgaven <span className="text-[#D9691E]">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              rows={4}
              required
              value={formData.message}
              onChange={handleChange}
              placeholder="Beskriv gerne træets type, højde, placering i forhold til huse/veje og eventuelle særlige ønsker..."
              className={`w-full px-4 py-3 rounded-lg border text-sm text-[#14291E] bg-[#FDFCFB] placeholder-[#94A39B] transition-colors focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1E4231] ${
                errors.message ? "border-red-500 ring-1 ring-red-500" : "border-[#D6CEBF]"
              }`}
            />
            {errors.message && <p className="text-xs text-red-600 mt-1">{errors.message}</p>}
          </div>

          {/* Photo Upload Field */}
          <div>
            <label htmlFor="photos" className="block text-xs font-bold uppercase tracking-wider text-[#2C493B] mb-1.5">
              Vedhæft billeder <span className="text-xs font-normal normal-case text-[#6E887A]">(valgfrit – maks. 5 billeder)</span>
            </label>

            <input
              ref={fileInputRef}
              type="file"
              id="photos"
              name="photos"
              multiple
              accept=".png,.jpg,.jpeg,.webp,.gif,image/png,image/jpeg,image/webp,image/gif"
              onChange={handleFileInputChange}
              className="sr-only"
            />

            <div
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                  handleFilesAdded(Array.from(e.dataTransfer.files));
                }
              }}
              tabIndex={0}
              role="button"
              aria-label="Vælg eller slip billeder her"
              className="border-2 border-dashed border-[#D6CEBF] hover:border-[#23583C] bg-[#FAF8F5] hover:bg-[#F4EFE6] rounded-xl p-4 sm:p-5 text-center cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-[#1E4231]"
            >
              <div className="flex flex-col items-center justify-center gap-1.5">
                <div className="w-10 h-10 rounded-full bg-white shadow-sm border border-[#E0D7C9] text-[#1E4231] flex items-center justify-center text-base">
                  <i className="fa-solid fa-cloud-arrow-up" aria-hidden="true" />
                </div>
                <p className="text-sm font-semibold text-[#14291E]">
                  Klik her for at vælge billeder <span className="font-normal text-[#5F786B]">eller træk og slip dem her</span>
                </p>
                <p className="text-xs text-[#7A9184]">
                  JPG, PNG, WEBP eller GIF (maks. 10 MB pr. billede, 20 MB i alt)
                </p>
              </div>
            </div>

            {fileError && (
              <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1.5">
                <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                <span>{fileError}</span>
              </p>
            )}

            {/* Selected files preview */}
            {files.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="text-xs font-bold uppercase tracking-wider text-[#2C493B]">
                  Valgte billeder ({files.length}/5):
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {files.map((file, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-2.5 rounded-lg bg-[#F3EFE7] border border-[#DDD4C5] text-xs text-[#1E3E2E]"
                    >
                      <div className="flex items-center gap-2 truncate pr-2">
                        <i className="fa-solid fa-image text-[#D9691E] shrink-0" aria-hidden="true" />
                        <span className="truncate font-medium">{file.name}</span>
                        <span className="text-[#6D8577] shrink-0">
                          ({(file.size / (1024 * 1024)).toFixed(1)} MB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(idx);
                        }}
                        aria-label={`Fjern ${file.name}`}
                        className="text-[#8B2323] hover:text-red-700 p-1 hover:bg-white/50 rounded transition-colors shrink-0"
                      >
                        <i className="fa-solid fa-xmark text-sm" aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 px-6 rounded-xl bg-[#D9691E] hover:bg-[#C25B15] text-white font-bold text-base tracking-wide transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-3 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D9691E]"
            >
              {submitting ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                  <span>Sender henvendelse...</span>
                </>
              ) : (
                <span>Send forespørgsel</span>
              )}
            </button>
            <p className="text-center text-xs text-[#6F877B] mt-2.5">
              Uforpligtende henvendelse • Hurtigt svar • Professionel rådgivning
            </p>
            <p className="text-center text-[11px] text-[#8BA194] mt-2">
              Ved at indsende formularen accepterer du, at vi behandler dine oplysninger med henblik på at besvare din henvendelse. Læs vores{" "}
              <Link to="/privatlivspolitik" className="underline hover:text-[#D9691E] transition-colors">
                privatlivs- og persondatapolitik
              </Link>
              .
            </p>
          </div>
        </form>
      )}
    </div>
  );
}
